/**
 * Format Helpers
 * Utility functions for formatting community data
 */

import { jwtDecode } from 'jwt-decode';
import type { DecodedJWT } from '@/core/auth/types';

/**
 * Convert strategy type enum (string or number) to numeric value
 * Backend returns string enum names due to JsonStringEnumConverter
 * Database stores values 0,1,2 consistently
 * @param value - Strategy type as number or string enum name
 * @returns Numeric strategy type (0, 1, or 2)
 */
export function normalizeStrategyType(value: number | string): number {
  // If already a number, return as-is (database uses 0,1,2)
  if (typeof value === 'number') {
    return Math.max(0, Math.min(2, value));
  }

  // Convert string enum names to numeric values (0,1,2)
  switch (value) {
    case 'FixedPercentageEnergy':
      return 0;  // Energy Percentage
    case 'FixedCentsPerKwh':
      return 1;  // Energy Amount (Per kWh)
    case 'FixedPercentage':
      return 2;  // Total Percentage
    default:
      // Fallback: try to parse as number
      const parsed = parseInt(value);
      return isNaN(parsed) ? 0 : Math.max(0, Math.min(2, parsed));
  }
}

/**
 * Format discount value with proper symbol based on strategy type
 * @param value - Discount value
 * @param strategyType - 0=EnergyPercentage, 1=ByKwh, 2=TotalPercentage
 * @returns Formatted discount string
 */
export function formatDiscount(value: number | null, strategyType: number | string): string {
  if (value === null) return 'N/A';

  // Normalize strategy type to number
  const normalizedType = normalizeStrategyType(strategyType);

  switch (normalizedType) {
    case 0: // Energy Percentage
      return `${value}%`;
    case 1: // Energy Amount (cents per kWh)
      return `${value.toFixed(2)}/kWh`;
    case 2: // Total Percentage
      return `${value}%`;
    default:
      return String(value);
  }
}

/**
 * Format ISO date string to DD/MM/YYYY format
 * @param dateStr - ISO date string or null
 * @returns Formatted date or empty string
 */
export function formatDate(dateStr: string | null): string {
  if (!dateStr) return '';

  try {
    // Use en-GB locale for DD/MM/YYYY format
    return new Date(dateStr).toLocaleDateString('en-GB');
  } catch (error) {
    return 'Invalid date';
  }
}

/**
 * Format usage type as text
 * Handles both numeric values (0-4) and string enum names
 * @param usageType - Usage type enum value
 * @returns Formatted usage type string
 */
export function formatUsageType(usageType: number | string | null | undefined): string {
  if (usageType === null || usageType === undefined) {
    return 'Unknown';
  }

  // Handle string enum names from backend
  if (typeof usageType === 'string') {
    const normalized = usageType.toLowerCase();
    if (normalized === 'public') return 'Public';
    if (normalized === 'semipublic') return 'Semi Public';
    if (normalized === 'privatehouse') return 'Private House';
    if (normalized === 'sharedbuilding') return 'Shared Building';
  }

  // Handle numeric values (both 0-indexed and 1-indexed)
  const type = typeof usageType === 'number' ? usageType : parseInt(String(usageType));

  if (!isNaN(type)) {
    switch (type) {
      case 0: // 0-indexed: Public
      case 1: // 1-indexed: Public
        return 'Public';
      case 2: // Both: Semi Public
        return 'Semi Public';
      case 3: // Both: Private House
        return 'Private House';
      case 4: // 1-indexed: Shared Building
        return 'Shared Building';
    }
  }

  console.warn('Unknown usageType value:', usageType);
  return 'Unknown';
}

/**
 * Format strategy type as text
 * @param strategyType - Strategy type (0=EnergyPercentage, 1=EnergyAmount, 2=TotalPercentage)
 * @returns Formatted strategy type string
 */
export function formatStrategyType(strategyType: number | string): string {
  const normalized = normalizeStrategyType(strategyType);

  switch (normalized) {
    case 0:
      return 'Energy Percentage';
    case 1:
      return 'Energy Amount (Per kWh)';
    case 2:
      return 'Total Percentage';
    default:
      return 'Unknown';
  }
}

/**
 * Extract user name from JWT token
 * @param token - JWT token string or null
 * @returns User name or "System"
 */
export function getUserNameFromToken(token: string | null): string {
  if (!token) return 'System';

  try {
    const decoded = jwtDecode<DecodedJWT>(token);
    return (
      decoded.OperatorId?.toString() ||
      decoded.operatorId?.toString() ||
      decoded.actor?.toString() ||
      'System'
    );
  } catch (error) {
    console.warn('Failed to decode token for user name:', error);
    return 'System';
  }
}
